import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import compact from 'lodash/compact';
import last from 'lodash/last';
import map from 'lodash/map';
import { excludeConcept, getOverlayImages } from '../../data/Concepts';
import type { Concept } from '../../data/Concepts';
import { getMapOverlayImage } from '../../assets/Assets';
import type { ImageKey } from '../../assets/Assets';
import ConceptOverlayAnimation from '../../assets/styles/ConceptOverlay.module.css';

const BackgroundImage = styled.div<{ image: string }>`
	background-image: url(${(props) => props.image});
	background-size: cover;
	bottom: 0;
	height: 100%;
	left: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	top: 0;
`;

export interface StateProps {
	inactiveConcepts: Concept[];
	activeConcepts: Concept[];
}

export interface DispatchProps {}

export interface OwnProps {}

type Props = StateProps & DispatchProps & OwnProps;

const getBackgroundImage = (imageKey: ImageKey, className?: string) => {
	return (
		<BackgroundImage
			key={imageKey}
			image={getMapOverlayImage(imageKey)}
			className={className}
		/>
	);
};

const ConceptsOverlayMapLayer: FunctionComponent<Props> = (props: Props) => {
	const { inactiveConcepts, activeConcepts } = props;

	const lastActivatedConcept = last(activeConcepts);

	const inactiveConceptImages = useMemo(() => {
		const imageKeys = getOverlayImages(
			compact(
				map(
					inactiveConcepts,
					(concept) => concept.images.overlay?.map?.inactive
				)
			)
		);
		return map(imageKeys, getBackgroundImage);
	}, [inactiveConcepts]);

	const removedInactiveConceptImage = useMemo(() => {
		const imageKey = lastActivatedConcept?.images.overlay?.map?.inactive?.key;
		if (imageKey) {
			return getBackgroundImage(imageKey, ConceptOverlayAnimation.disappear);
		} else {
			return null;
		}
	}, [lastActivatedConcept]);

	const activeConceptImages = useMemo(() => {
		const imageKeys = getOverlayImages(
			compact(
				map(
					excludeConcept(activeConcepts, lastActivatedConcept),
					(concept) => concept.images.overlay?.map?.active
				)
			)
		);
		return map(imageKeys, getBackgroundImage);
	}, [activeConcepts, lastActivatedConcept]);

	const addedActiveConceptImage = useMemo(() => {
		const imageKey = lastActivatedConcept?.images.overlay?.map?.active?.key;
		if (imageKey) {
			return getBackgroundImage(imageKey, ConceptOverlayAnimation.appear);
		} else {
			return null;
		}
	}, [lastActivatedConcept]);

	return (
		<>
			{inactiveConceptImages}
			{removedInactiveConceptImage}
			{activeConceptImages}
			{addedActiveConceptImage}
		</>
	);
};

export default ConceptsOverlayMapLayer;
